import React from "react";
import { Space } from "antd";
import { AxAvatar, AxDivider, AxButton } from "@dynata/axiom3";

class Avatar extends React.Component {
  render() {
    return (
      <div className="section" id="avatar">
        <h3>Avatar</h3>
        <p>
          Avatars can be used to represent people or objects. It supports
          images, icons, or letters.
        </p>

        <AxDivider orientation="left" plain>
          Basic Usage
        </AxDivider>

        <Space size="large" direction="vertical">
          <pre>{`<AxAvatar icon={<i className="fa-regular fa-user"></i>} />`}</pre>
          <div className="prop-header">
            <p className="props-title">Props</p>
          </div>
          <table className="props-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Description</th>
                <th>Type</th>
                <th>Default</th>
                <th>Version</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>alt</td>
                <td>
                  This attribute defines the alternative text describing the
                  image
                </td>
                <td>string</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>gap</td>
                <td>Letter type unit distance between left and right sides</td>
                <td>number</td>
                <td>4</td>
                <td>4.3.0</td>
              </tr>
              <tr>
                <td>icon</td>
                <td>
                  Any FontAwesomePro Icon from Light, Regular and Solid types,
                  can be used as Avatar Icon.
                </td>
                <td>ReactNode</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>shape</td>
                <td>The shape of avatar</td>
                <td>
                  <code>circle</code> | <code>square</code>
                </td>
                <td>
                  <code>circle</code>
                </td>
                <td></td>
              </tr>
              <tr>
                <td>text</td>
                <td>To show initials etc.</td>
                <td>string</td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td>size</td>
                <td>The size of the avatar</td>
                <td>
                  number | <code>large</code> | <code>small</code> |{" "}
                  <code>default</code> |{" "}
                  <code>{`{ xs: number, sm: number, ...}`}</code>
                </td>
                <td>
                  <code>default</code>
                </td>
                <td>4.7.0</td>
              </tr>
              <tr>
                <td>src</td>
                <td>
                  The address of the image for an image avatar or image element
                </td>
                <td>string | ReactNode</td>
                <td>-</td>
                <td>ReactNode: 4.8.0</td>
              </tr>
              <tr>
                <td>srcSet</td>
                <td>
                  A list of sources to use for different screen resolutions
                </td>
                <td>string</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>draggable</td>
                <td>Whether the picture is allowed to be dragged</td>
                <td>
                  boolean | <code>'true'</code> | <code>'false'</code>
                </td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>crossOrigin</td>
                <td>CORS settings attributes</td>
                <td>
                  <code>'anonymous'</code> | <code>'use-credentials'</code> |{" "}
                  <code>''</code>
                </td>
                <td>-</td>
                <td>4.17.0</td>
              </tr>
              <tr>
                <td>onError</td>
                <td>
                  Handler when img load error, return false to prevent default
                  fallback behavior
                </td>
                <td>() =&gt; boolean</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>...others</td>
                <td>
                  All extra props are passed down to the{" "}
                  <>
                    <AxButton
                      size="small"
                      type="link"
                      target="_blank"
                      href="https://4x.ant.design/components/avatar/#API"
                    >
                      {`<Avatar />`}
                    </AxButton>
                  </>{" "}
                  component.
                </td>
                <td></td>
                <td>-</td>
                <td></td>
              </tr>
            </tbody>
          </table>

          <div className="prop-header">
            <p className="props-title">Props: Avatar.Group</p>
          </div>

          <table className="props-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Description</th>
                <th>Type</th>
                <th>Default</th>
                <th>Version</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>maxCount</td>
                <td>Max avatars to show</td>
                <td>number</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>maxPopoverPlacement</td>
                <td>The placement of excess avatar Popover</td>
                <td>
                  <code>top</code> | <code>bottom</code>
                </td>
                <td>
                  <code>top</code>
                </td>
                <td></td>
              </tr>
              <tr>
                <td>maxPopoverTrigger</td>
                <td>Set the trigger of excess avatar Popover</td>
                <td>
                  <code>hover</code> | <code>focus</code> | <code>click</code>
                </td>
                <td>
                  <code>hover</code>
                </td>
                <td>4.17.0</td>
              </tr>
              <tr>
                <td>maxStyle</td>
                <td>The style of excess avatar style</td>
                <td>CSSProperties</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>size</td>
                <td>The size of the avatar</td>
                <td>
                  number | <code>large</code> | <code>small</code> |{" "}
                  <code>default</code> |{" "}
                  <code>{`{ xs: number, sm: number, ...}`}</code>
                </td>
                <td>
                  <code>default</code>
                </td>
                <td>4.8.0</td>
              </tr>
            </tbody>
          </table>

          <AxDivider orientation="left" plain>
            Circle Shape
          </AxDivider>

          <Space size="large" direction="vertical">
            <pre>{`
<AxAvatar size="small" icon={<i className="fa-regular fa-user"></i>} />
<AxAvatar icon={<i className="fa-regular fa-user"></i>} />
<AxAvatar size="large" icon={<i className="fa-regular fa-user"></i>} />
<AxAvatar size={64} icon={<i className="fa-regular fa-user"></i>} />
<AxAvatar
  size={{ xs: 24, sm: 32, md: 40, lg: 64, xl: 80, xxl: 100 }}
  icon={<i className="fa-regular fa-user"></i>}
/>`}</pre>
            <Space>
              <AxAvatar
                size="small"
                icon={<i className="fa-regular fa-user"></i>}
              />
              <AxAvatar icon={<i className="fa-regular fa-user"></i>} />
              <AxAvatar
                size="large"
                icon={<i className="fa-regular fa-user"></i>}
              />
              <AxAvatar
                size={64}
                icon={<i className="fa-regular fa-user"></i>}
              />
              <AxAvatar
                size={{ xs: 24, sm: 32, md: 40, lg: 64, xl: 80, xxl: 100 }}
                icon={<i className="fa-regular fa-user"></i>}
              />
            </Space>
          </Space>

          <AxDivider orientation="left" plain>
            Square Shape
          </AxDivider>

          <Space size="large" direction="vertical">
            <pre>{`
<AxAvatar
  shape="square"
  size="small"
  icon={<i className="fa-regular fa-user"></i>}
/>
<AxAvatar shape="square" icon={<i className="fa-regular fa-user"></i>} />
<AxAvatar
  shape="square"
  size="large"
  icon={<i className="fa-regular fa-user"></i>}
/>
<AxAvatar
  shape="square"
  size={64}
  icon={<i className="fa-regular fa-user"></i>}
/>
<AxAvatar
  shape="square"
  size={{ xs: 24, sm: 32, md: 40, lg: 64, xl: 80, xxl: 100 }}
  icon={<i className="fa-regular fa-user"></i>}
/>`}</pre>
            <Space>
              <AxAvatar
                shape="square"
                size="small"
                icon={<i className="fa-regular fa-user"></i>}
              />
              <AxAvatar
                shape="square"
                icon={<i className="fa-regular fa-user"></i>}
              />
              <AxAvatar
                shape="square"
                size="large"
                icon={<i className="fa-regular fa-user"></i>}
              />
              <AxAvatar
                shape="square"
                size={64}
                icon={<i className="fa-regular fa-user"></i>}
              />
              <AxAvatar
                shape="square"
                size={{ xs: 24, sm: 32, md: 40, lg: 64, xl: 80, xxl: 100 }}
                icon={<i className="fa-regular fa-user"></i>}
              />
            </Space>
          </Space>

          <AxDivider orientation="left" plain>
            Other Options
          </AxDivider>

          <Space size="large" direction="vertical">
            <pre>{`
<AxAvatar icon={<i className="fa-regular fa-user"></i>} />
<AxAvatar>U</AxAvatar>
<AxAvatar size={40}>USER</AxAvatar>
<AxAvatar src="https://joeschmoe.io/api/v1/random" />
<AxAvatar style={{ backgroundColor: "#1e528f" }}>U</AxAvatar>
<AxAvatar
  style={{ backgroundColor: "#D0368B" }}
  icon={<i className="fa-regular fa-user"></i>}
/>`}</pre>
            <Space>
              <AxAvatar icon={<i className="fa-regular fa-user"></i>} />
              <AxAvatar>U</AxAvatar>
              <AxAvatar size={40}>USER</AxAvatar>
              <AxAvatar src="https://joeschmoe.io/api/v1/random" />
              <AxAvatar style={{ backgroundColor: "#1e528f" }}>U</AxAvatar>
              <AxAvatar
                style={{ backgroundColor: "#D0368B" }}
                icon={<i className="fa-regular fa-user"></i>}
              />
            </Space>
          </Space>

          <AxDivider orientation="left" plain>
            Group
          </AxDivider>

          <Space size="large" direction="vertical">
            <pre>{`
<AxAvatar.Group
maxCount={2}
maxStyle={{ color: "#D0368B", backgroundColor: "#FCEEF6" }}
>
  <AxAvatar src="https://joeschmoe.io/api/v1/random" />
  <AxAvatar style={{ backgroundColor: "#1e528f" }}>K</AxAvatar>
  <AxAvatar style={{ backgroundColor: "#D0368B" }}>K</AxAvatar>
  <AxAvatar
    style={{ backgroundColor: "#1890ff" }}
    icon={<i className="fa-regular fa-user"></i>}
  />
</AxAvatar.Group>`}</pre>
            <Space>
              <AxAvatar.Group
                maxCount={2}
                maxStyle={{ color: "#D0368B", backgroundColor: "#FCEEF6" }}
              >
                <AxAvatar src="https://joeschmoe.io/api/v1/random" />
                <AxAvatar style={{ backgroundColor: "#1e528f" }}>K</AxAvatar>
                <AxAvatar style={{ backgroundColor: "#D0368B" }}>K</AxAvatar>
                <AxAvatar
                  style={{ backgroundColor: "#1890ff" }}
                  icon={<i className="fa-regular fa-user"></i>}
                />
              </AxAvatar.Group>
            </Space>
          </Space>
        </Space>
      </div>
    );
  }
}
export default Avatar;
